import { Link } from "react-router-dom";
import { firstToCheck, type CheckState, type SetupCheck } from "../lib/setup-checks";
import { RelativeTime, StatusPill } from "./ui";

const STATE: Record<CheckState, { text: string; tone: "ok" | "warning" | "critical" | "neutral" }> = {
  passed: { text: "done", tone: "ok" },
  failing: { text: "not done", tone: "critical" },
  waiting: { text: "waiting", tone: "warning" },
  unknown: { text: "not known yet", tone: "neutral" },
};

/**
 * #348: an empty view that says why. The title says what is missing; the
 * line under it names the first check to act on (the first proven failing,
 * else the first not yet known); the ordered list is every precondition,
 * each with what central knows, what to do, and the exact setting.
 */
export function SetupChecklist({ title, checks, note }: { title: string; checks: SetupCheck[]; note?: string }) {
  const first = firstToCheck(checks);
  return (
    <section aria-label={title} className="rounded-xl border border-dashed border-neutral-300 p-5 text-sm text-neutral-700 dark:border-neutral-700 dark:text-neutral-300">
      <p className="text-base font-medium text-ink dark:text-neutral-100">{title}</p>
      {first && (
        <p className="mt-1">
          {first.state === "unknown" ? "Check first" : first.state === "waiting" ? "Waiting on" : "Not done"}: <span className="font-medium">{first.label}</span>.
        </p>
      )}
      {note && <p className="mt-1 text-neutral-600 dark:text-neutral-400">{note}</p>}
      <ol aria-label="What to check, in order" className="mt-3 list-decimal space-y-3 pl-5">
        {checks.map((c) => (
          <li key={c.id}>
            <p>
              <span className="font-medium text-ink dark:text-neutral-100">{c.label}</span> <StatusPill status={STATE[c.state].text} tone={STATE[c.state].tone} />
            </p>
            {c.evidence && (
              <p className="mt-0.5 text-neutral-600 dark:text-neutral-400">
                {c.evidence}
                {c.at && <> <RelativeTime iso={c.at} /></>}
                {c.at && "."}
              </p>
            )}
            {c.state !== "passed" && <p className="mt-0.5">{c.fix}</p>}
            {(c.setting || c.href) && (
              <p className="mt-0.5 text-xs text-neutral-600 dark:text-neutral-400">
                {c.setting && <>Setting: <code className="rounded bg-neutral-100 px-1 font-mono dark:bg-neutral-800">{c.setting}</code></>}
                {c.setting && c.href && " · "}
                {c.href && <Link to={c.href} className="text-accent-strong underline dark:text-accent-bright">{c.linkText ?? "Deploy guide"}</Link>}
              </p>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}
